import { ProductApprovalState } from '@texas/api/endpoints/productApprovalsApi';
import { useState, useEffect } from 'react';
import { SupplierCoverageHeatmapViewMode } from './types';

export interface WidgetFilters {
  sampleSize: number;
  customerId: number | null;
  customerIds: number[];
  branchIds: number[];
  paState: ProductApprovalState | null;
  supplierId: number | null;
  materialId: number | null;
  heatmapViewMode: SupplierCoverageHeatmapViewMode;
  articleCreatedAfter: string | null;
}

const DEFAULT_FILTERS: WidgetFilters = {
  sampleSize: 200,
  customerId: null,
  customerIds: [],
  branchIds: [],
  paState: null,
  supplierId: null,
  materialId: null,
  heatmapViewMode: 'suppliers',
  articleCreatedAfter: null,
};

export function useWidgetStorage(widgetKey: string) {
  const storageKey = `widget-filters-${widgetKey}`;

  const [filters, setFilters] = useState<WidgetFilters>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          ...DEFAULT_FILTERS,
          ...parsed,
        };
      }
    } catch {
      return DEFAULT_FILTERS;
    }
    return DEFAULT_FILTERS;
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(filters));
    } catch {
      // Silently fail if localStorage is unavailable
    }
  }, [filters, storageKey]);

  const updateSampleSize = (sampleSize: number) => {
    setFilters((prev) => ({ ...prev, sampleSize }));
  };

  const updateCustomerId = (customerId: number | null) => {
    setFilters((prev) => ({ ...prev, customerId }));
  };

  const updateCustomerIds = (customerIds: number[]) => {
    setFilters((prev) => ({ ...prev, customerIds }));
  };

  const updateBranchIds = (branchIds: number[]) => {
    setFilters((prev) => ({ ...prev, branchIds }));
  };

  const updatePaState = (paState: ProductApprovalState | null) => {
    setFilters((prev) => ({ ...prev, paState }));
  };

  const updateSupplierId = (supplierId: number | null) => {
    setFilters((prev) => ({ ...prev, supplierId }));
  };

  const updateMaterialId = (materialId: number | null) => {
    setFilters((prev) => ({ ...prev, materialId }));
  };

  const updateHeatmapViewMode = (
    heatmapViewMode: SupplierCoverageHeatmapViewMode,
  ) => {
    setFilters((prev) => ({ ...prev, heatmapViewMode }));
  };

  const updateArticleCreatedAfter = (articleCreatedAfter: string | null) => {
    setFilters((prev) => ({ ...prev, articleCreatedAfter }));
  };

  return {
    filters,
    updateSampleSize,
    updateCustomerId,
    updateCustomerIds,
    updateBranchIds,
    updatePaState,
    updateSupplierId,
    updateMaterialId,
    updateHeatmapViewMode,
    updateArticleCreatedAfter,
  };
}
